import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useSidebar } from "@/App";
import { 
  Calendar, Users, LogOut, LayoutDashboard, Plus, 
  CalendarDays, Wrench, Columns, FileText, ChevronLeft, ChevronRight, FolderOpen, Settings, UserCircle, Car, Menu, X, Calculator, Briefcase, MessageSquare, Package
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import NotificationBell from "./NotificationBell";
import { useState, useEffect } from "react";

export default function Sidebar() {
  const { user, logout, hasMenuAccess } = useAuth();
  const { collapsed, toggle } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Menu items with permission-based visibility
  const menuItems = [
    { icon: Plus, label: "Nouveau RDV", path: "/vehicles/new", menuKey: "new_rdv" },
    { icon: Briefcase, label: "Au Bureau", path: "/au-bureau", menuKey: "au_bureau" },
    { icon: LayoutDashboard, label: "Planning", path: "/dashboard", menuKey: "planning" },
    { icon: Columns, label: "Kanban", path: "/kanban", menuKey: "kanban" },
    { icon: FolderOpen, label: "Dossiers", path: "/dossiers", menuKey: "dossiers" },
    { icon: UserCircle, label: "Clients", path: "/clients", menuKey: "clients" },
    { icon: Car, label: "Véhicules", path: "/vehicules-db", menuKey: "vehicules" },
    { icon: CalendarDays, label: "Aujourd'hui", path: "/today", menuKey: "today" },
    { icon: Calculator, label: "Rentabilité", path: "/rentabilite", menuKey: "rentabilite" },
    { icon: MessageSquare, label: "Journal SMS", path: "/sms", menuKey: "sms" },
    { icon: Package, label: "Pièces", path: "/pieces", menuKey: "pieces" },
    { icon: Settings, label: "Paramètres", path: "/settings", menuKey: "parametres" },
  ];

  const visibleMenuItems = menuItems.filter(item => hasMenuAccess(item.menuKey));

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getSpecialiteLabel = (spec) => {
    const labels = {
      admin: "Responsable",
      carrossier: "Carrossier",
      peintre: "Peintre",
      mecanicien: "Mécanicien",
      secretaire: "Secrétaire",
      responsable: "Responsable",
    };
    return labels[spec] || spec;
  };

  // Mobile Header Bar
  if (isMobile) {
    return (
      <>
        {/* Fixed Mobile Header */}
        <header className="fixed top-0 left-0 right-0 h-14 bg-slate-900 text-white flex items-center justify-between px-4 z-50 shadow-lg">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 hover:bg-slate-800 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm">Auto Gardoise</span>
          </div>
          
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/50"
              onClick={() => setMobileMenuOpen(false)}
            />
            
            {/* Slide-in Menu */}
            <aside className="absolute left-0 top-0 h-full w-72 bg-slate-900 text-white flex flex-col animate-slide-in">
              {/* Close button */}
              <div className="p-4 flex items-center justify-between border-b border-slate-700">
                <div className="bg-white rounded-lg p-2 flex-1 mr-4">
                  <img 
                    src="/logo.png" 
                    alt="Logo" 
                    className="w-full h-auto max-h-10 object-contain" 
                  />
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* User info */}
              <div className="p-4 border-b border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center font-bold">
                    {user?.prenom?.[0]}{user?.nom?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-slate-400">{getSpecialiteLabel(user?.specialite)}</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                {visibleMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleNavigation(item.path)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors",
                        isActive 
                          ? "bg-red-600 text-white" 
                          : "text-slate-300 hover:bg-slate-800"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Logout */}
              <div className="p-3 border-t border-slate-700">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-medium">Déconnexion</span>
                </button>
              </div>
            </aside>
          </div>
        )}
      </>
    );
  }

  // Desktop Sidebar
  return (
    <aside 
      className={cn(
        "fixed left-0 top-0 h-screen bg-slate-900 text-white flex flex-col transition-all duration-300 z-50",
        collapsed ? "w-16" : "w-64"
      )} 
      data-testid="sidebar"
    >
      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggle}
        className="absolute -right-3 top-6 bg-slate-800 hover:bg-slate-700 rounded-full w-6 h-6 p-0 border border-slate-600"
        data-testid="toggle-sidebar"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </Button>

      {/* Logo */}
      <div className={cn("p-3 border-b border-slate-700", collapsed && "px-2")}>
        {collapsed ? (
          <div className="bg-white rounded p-1">
            <div className="text-xs font-black text-slate-800 text-center">AG</div>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_bodyshop-planner-1/artifacts/ol1hf808_LOGO_Carrosserie%20Auto%20Gardoise_GRIS.PNG" 
              alt="Carrosserie Auto Gardoise" 
              className="w-full h-auto max-h-16 object-contain" 
            />
          </div>
        )}
      </div>

      {/* User info */}
      {!collapsed && (
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center font-bold flex-shrink-0">
              {user?.prenom?.[0]}{user?.nom?.[0]}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm truncate">{user?.prenom} {user?.nom}</p>
              <p className="text-xs text-slate-400 truncate">{getSpecialiteLabel(user?.specialite)}</p>
            </div>
            <NotificationBell />
          </div>
        </div>
      )}
      
      {/* Collapsed notification bell */}
      {collapsed && (
        <div className="p-2 border-b border-slate-700 flex justify-center">
          <NotificationBell />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.path.replace('/', '')}`}
              onClick={() => handleNavigation(item.path)}
              title={collapsed ? item.label : undefined}
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors",
                isActive 
                  ? "bg-red-600 text-white" 
                  : "text-slate-300 hover:bg-slate-800 hover:text-white",
                collapsed && "justify-center px-2"
              )}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span className="font-medium truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-2 border-t border-slate-700">
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          title={collapsed ? "Déconnexion" : undefined}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-red-600 hover:text-white transition-colors",
            collapsed && "justify-center px-2"
          )}
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {!collapsed && <span className="font-medium">Déconnexion</span>}
        </button>
      </div>
    </aside>
  );
}
